import { useBoolean, useConst } from '@fluentui/react-hooks';
import { PanelType } from 'office-ui-fabric-react/lib/Panel';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationsTarget, useNotificationsManager } from '../../contexts/notificationContext.js';
import { Data } from '../../data/Data.jsx';
import { DataIntervenantEXTSociete } from '../../data/DataIntervenantEXTSociete.jsx';
import { MaxLengths } from '../../data/MaxLenghts';
import { Immocom } from '../../specifique/immocom';
import { ComboBoxAgence } from '../ComboBox/ComboBoxAgence.jsx';
import { ComboBoxRegion } from '../ComboBox/ComboBoxRegion.jsx';
import { EUIPanel } from '../EUI/EUIPanel.jsx';
import { EUITextField } from '../EUI/EUITextField';
import { PickerCPVille } from '../Picker/PickerCPVille';

const FicheIntervenantEXTSociete = (props) => {

    // CONTEXTS
    const { clear, dismiss, extract, load } = useNotificationsManager();

    // PROPS
    const {
        idIntervenantEXTSociete = undefined,
        isOpen = false,
        /* --- */
        onDismiss = () => null,
        onSave = () => null,
    } = props;

    // CONSTANTES
    const _defaultIntervenantEXTSociete = useConst({});

    // STATES
    const [_isFormValid, { setFalse: _setFormIsNotValid, setTrue: _setFormIsValid }] = useBoolean(false);
    const [_intervenantEXTSociete, _setIntervenantEXTSociete] = useState({ ..._defaultIntervenantEXTSociete });

    // REFS
    const _isMounted = useRef(false); // Utilisé pour bloquer certains useEffect au premier render

    // METHODS
    const _chargementDonnees = useCallback(() => {
        const id = load("Chargement de l'intervenant en cours...");
        DataIntervenantEXTSociete._detailler(idIntervenantEXTSociete).then(response => {
            const onSuccess = () => {
                const intervenant = response.reponseAPIobjet;
                _setIntervenantEXTSociete(intervenant);
            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [idIntervenantEXTSociete]);

    const _resetState = useCallback(() => {
        _setIntervenantEXTSociete({ ..._defaultIntervenantEXTSociete });
    }, []);

    const _onDismiss = useCallback(() => {
        onDismiss();
    }, [onDismiss]);

    const _onOpen = useCallback(() => {
        _resetState();
        if (!!idIntervenantEXTSociete) _chargementDonnees();
    }, [_resetState, idIntervenantEXTSociete]);

    const _onSave = useCallback(() => {
        const id = load("Enregistrement du intervenant en cours...");
        DataIntervenantEXTSociete._enregistrer(_intervenantEXTSociete).then(response => {
            const onSuccess = () => {
                onSave();
                _resetState();
            }
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [_intervenantEXTSociete, _resetState, idIntervenantEXTSociete, onSave]);

    const _onVerificationFormulaire = useCallback(() => {
        clear();
        _setFormIsNotValid();
        Data._onValidationFormulaire("IntervenantEXTSociete", _intervenantEXTSociete, !!idIntervenantEXTSociete ? "Modification" : "Création").then(response => {
            const onSuccess = _setFormIsValid;
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
        });
    }, [_intervenantEXTSociete, idIntervenantEXTSociete]);

    // EFFECTS (ComponentDidUpdate)
    // Vérification des données du formulaire
    useEffect(() => {
        if (_isMounted.current)
            _onVerificationFormulaire();
    }, [_isMounted, _intervenantEXTSociete]);

    // EFFECTS (ComponentDidMount)
    useEffect(() => {
        _isMounted.current = true;
    }, []);

    // RENDER
    return <EUIPanel
        headerText={!!idIntervenantEXTSociete ? "Modification d'une société" : "Nouvelle société"}
        isFormValid={_isFormValid}
        isOpen={isOpen}
        onDismiss={_onDismiss}
        onOpen={_onOpen}
        onSave={_onSave}
        type={PanelType.medium}
    >

        <EUITextField
            label="Nom"
            maxLength={MaxLengths.intervenantEXTSociete.nom}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, nom: newValue }))}
            required
            value={_intervenantEXTSociete.nom}
        />

        <EUITextField
            label="Forme juridique"
            maxLength={MaxLengths.intervenantEXTSociete.formeJuridique}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, formeJuridique: newValue }))}
            required
            value={_intervenantEXTSociete.formeJuridique}
        />

        <EUITextField
            label="SIREN"
            maxLength={MaxLengths.intervenantEXTSociete.siren}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, siren: newValue }))}
            placeholder="Numéro"
            value={_intervenantEXTSociete.siren}
        />

        <EUITextField
            maxLength={MaxLengths.intervenantEXTSociete.sirenEtablissement}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, sirenEtablissement: newValue }))}
            placeholder="Etablissement"
            value={_intervenantEXTSociete.sirenEtablissement}
        />

        <EUITextField
            maxLength={MaxLengths.intervenantEXTSociete.sirenVille}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, sirenVille: newValue }))}
            placeholder="Ville"
            value={_intervenantEXTSociete.sirenVille}
        />

        <ComboBoxRegion
            onChange={(event, option) => _setIntervenantEXTSociete(prev => ({ ...prev, idRegion: option.key, idAgence: undefined }))}
            selectedKey={_intervenantEXTSociete.idRegion}
        />

        <ComboBoxAgence
            idRegion={_intervenantEXTSociete.idRegion}
            onChange={(event, option) => _setIntervenantEXTSociete(prev => ({ ...prev, idAgence: option.key }))}
            selectedKey={_intervenantEXTSociete.idAgence}
        />

        <EUITextField
            label="Adresse"
            maxLength={MaxLengths.intervenantEXTSociete.adresse}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, adresse: newValue }))}
            required
            value={_intervenantEXTSociete.adresse}
        />

        <PickerCPVille
            cp={_intervenantEXTSociete.cp}
            onChange={(idVilleAdministrative, idDepartementAdministratif, idRegionAdministrative, cp, villeAdministrative) => _setIntervenantEXTSociete(prev => ({ ...prev, cp, ville: villeAdministrative }))}
            required
            villeAdministrative={_intervenantEXTSociete.ville}
        />

        <EUITextField
            label="Pays"
            maxLength={MaxLengths.intervenantEXTSociete.pays}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, pays: newValue }))}
            value={_intervenantEXTSociete.pays}
        />

        <EUITextField
            label="Téléphone"
            maxLength={MaxLengths.intervenantEXTSociete.tel}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, tel: newValue }))}
            value={_intervenantEXTSociete.tel}
        />

        <EUITextField
            label="Fax"
            maxLength={MaxLengths.intervenantEXTSociete.fax}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, fax: newValue }))}
            value={_intervenantEXTSociete.fax}
        />

        <EUITextField
            label="Email"
            maxLength={MaxLengths.intervenantEXTSociete.email}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTSociete(prev => ({ ...prev, email: newValue }))}
            value={_intervenantEXTSociete.email}
        />

    </EUIPanel>;

};

export default FicheIntervenantEXTSociete;